// The sign-in and invitation pages' calls: JSON to the api on this origin, before any session (ADR-0011, ADR-0034).
import { ApiError, call, type Fetch } from "../http.js";
import type { Language } from "../i18n/language.js";

/** A business a person may sign in to, by its name; null where the api does not know it. */
export interface BusinessChoice {
  readonly tenantId: string;
  readonly name: string | null;
}

/** What a right code did: signed the person in, or names the businesses it is right for, to choose one. */
export type CodeChecked = { readonly signedIn: true } | { readonly choose: readonly BusinessChoice[] };

/** An invitation still open, as its link shows it: the business and the Roles the person is invited to. */
export interface OpenInvitation {
  readonly tenantId: string;
  readonly kind: "email" | "mobile";
  readonly business: string | null;
  readonly roles: readonly string[];
}

/**
 * The consent message a PIN is kept with, confirmed: the language of the Privacy Notice edition it linked. The api
 * records it against the current notice version (#698).
 */
export interface PinConsentGiven {
  readonly noticeLanguage: Language;
}

export interface SignInApi {
  /** Emails a code to every business the email may sign in to, and to `business` when a link names one. */
  requestCode(email: string, business?: string): Promise<void>;
  /** Checks the code: across every business it was sent for, or in `business` alone, as when one is chosen. */
  verifyCode(email: string, code: string, trustDevice: boolean, business?: string): Promise<CodeChecked>;
  /** The invitation an invite link carries; undefined when it is used, expired or not one. */
  openInvitation(code: string): Promise<OpenInvitation | undefined>;
  /**
   * Accepts an email invitation on this device: the person is signed in. Rejects when it is used or expired. A PIN is
   * kept only with the consent confirmed for it (#698).
   */
  acceptInvitation(code: string, trustDevice: boolean, pin?: string, consent?: PinConsentGiven): Promise<void>;
}

export function createSignInApi(fetcher: Fetch = (input, init) => fetch(input, init)): SignInApi {
  return {
    async requestCode(email, business) {
      await call(fetcher, "/auth/code", { json: { email, ...(business ? { tenantId: business } : {}) } });
    },
    async verifyCode(email, code, trustDevice, business) {
      const answer = await call<{ choose?: BusinessChoice[] }>(fetcher, "/auth/verify", {
        json: { email, code, trustDevice, ...(business ? { tenantId: business } : {}) },
      });
      return answer.choose ? { choose: answer.choose } : { signedIn: true };
    },
    async openInvitation(code) {
      try {
        return await call<OpenInvitation>(fetcher, "/auth/invitation", { json: { code } });
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return undefined;
        throw error;
      }
    },
    async acceptInvitation(code, trustDevice, pin, consent) {
      await call(fetcher, "/auth/invitation/accept", {
        json: { code, trustDevice, ...(pin ? { pin, ...(consent ? { consent } : {}) } : {}) },
      });
    },
  };
}

/**
 * Whether accepting was refused for too many tries: the api's sign-in routes answer 429 `too_many_requests` once a
 * client has failed too often (`tooMany` in apps/api/src/auth/http.ts). Checked against the real route in
 * tests/signup/invitation-too-many.eval.test.ts, so the two cannot drift apart (#912).
 */
export const isTooManyTries = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 429 && error.code === "too_many_requests";
